import {
  FunctionComponent,
  MouseEvent,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import styles from "./NotificationComponent.module.css";
import { useApolloClient, useMutation } from "@apollo/client";
import {
  CLEAR_ALL_NOTIFICATION,
  UPDATE_READ_STATUS_BATCH,
  GET_NOTIFICATIONS,
} from "./api/NotificationQuery";
import { notificationData } from "../../../helper";
import { CircularProgress } from "@mui/material";
import { globalContext } from "../../../Provider/AppProvider";
import moment from "moment";
import "moment-timezone";
import images from "../../../assets/images";
import { useNavigate } from "react-router-dom";
import { IoClose } from "react-icons/io5";
import {
  formatCustomerType,
  formatNotesForDisplay,
  truncateNotePreview,
} from "../../../utils/listing-alert-display";
import { getNotificationTypeLabel } from "../../../utils/notification-feed-labels";

const PAGE_SIZE = 20;

interface NotificationProps {
  closePopup: () => void;
  preferredPeriod?: "30D" | "90D" | "12M" | "All";
  statusFilters?: {
    pending: boolean;
    confirmed: boolean;
    fulfilled: boolean;
    cancelled: boolean;
    refunded: boolean;
  };
}

interface GroupedNotification {
  groupId: string;
  type: string;
  title: string;
  count: number;
  feedIds: string[];
  notification: string;
  ticket: any;
  ticket_placement: any;
  tp_id_array: any;
  latestFeedId: string;
}

const NotificationComponent: FunctionComponent<NotificationProps> = ({
  closePopup,
  preferredPeriod = "All",
  statusFilters = {
    pending: true,
    confirmed: true,
    fulfilled: true,
    cancelled: true,
    refunded: true,
  },
}) => {
  const normalizePeriod = (period: string): "30D" | "90D" | "12M" | "All" => {
    if (["30D", "90D", "12M", "All"].includes(period)) {
      return period as "30D" | "90D" | "12M" | "All";
    }
    return "All";
  };

  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const { DefaultProfile } = images;
  const { globalState, handleGlobalState } = useContext<any>(globalContext);

  const [activeTab, setActiveTab] = useState<"activity" | "summary">("activity");
  const [selectedPeriod, setSelectedPeriod] = useState<"30D" | "90D" | "12M" | "All">(
    normalizePeriod(preferredPeriod)
  );
  const [hiddenGroupIds, setHiddenGroupIds] = useState<string[]>([]);

  const [notifications, setNotifications] = useState<notificationData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [recentCount, setRecentCount] = useState<number>(0);
  const [pastCount, setPastCount] = useState<number>(0);

  const notificationRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  // Start cursor from far future so we get the most recent notifications first
  const cursorRef = useRef<string>(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString());

  const getSinceDateByPeriod = (period: "30D" | "90D" | "12M" | "All"): string => {
    if (period === "All") return "1970-01-01T00:00:00.000Z";
    const baseDate = new Date();
    if (period === "12M") {
      baseDate.setMonth(baseDate.getMonth() - 12);
      return baseDate.toISOString();
    }
    const days = period === "30D" ? 30 : 90;
    baseDate.setDate(baseDate.getDate() - days);
    return baseDate.toISOString();
  };

  const loadData = useCallback(
    async (resetCursor = true) => {
      if (loading) return;

      const sinceDate = getSinceDateByPeriod(selectedPeriod);
      const untilDate = new Date().toISOString();
      const recentCutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      setLoading(true);
      if (resetCursor) {
        setInitialLoading(true);
        setNotifications([]);
        setHasMore(true);
        cursorRef.current = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();
      }

      try {
        const { data } = await apolloClient.query({
          query: GET_NOTIFICATIONS,
          variables: {
            userId: globalState?.profile?.id,
            sinceDate,
            untilDate,
            recentCutoff,
            pageSize: PAGE_SIZE,
            cursor: cursorRef.current,
          },
          fetchPolicy: "network-only",
        });

        const newBatch = data?.all || [];
        const batchSize = newBatch.length;

        if (batchSize > 0) {
          cursorRef.current = newBatch[batchSize - 1].notification;
        }

        setNotifications((prev) => {
          if (resetCursor) return newBatch;
          const existingIds = new Set(prev.map((n: notificationData) => n.f_id));
          const uniqueNew = newBatch.filter((n:  notificationData) => !existingIds.has(n.f_id));
          return [...prev, ...uniqueNew];
        });

        setHasMore(batchSize === PAGE_SIZE);
        setRecentCount(data?.currentCount?.aggregate?.count || 0);
        setPastCount(data?.pastCount?.aggregate?.count || 0);

        setInitialLoading(false);
        setLoading(false);
      } catch (error) {
        console.error("Failed to load notifications", error);
        setInitialLoading(false);
        setLoading(false);
        if (resetCursor) {
          setNotifications([]);
          setRecentCount(0);
          setPastCount(0);
          setHasMore(false);
        }
      }
    },
    [apolloClient, globalState?.profile?.id, selectedPeriod] // Removed 'loading' from dependencies
  );

  const loadMore = useCallback(() => {
    if (!hasMore || loading || initialLoading) return;
    void loadData(false); // Explicitly mark as async operation
  }, [hasMore, loading, initialLoading, loadData]);

  const filterKey = useMemo(() => {
    return `${selectedPeriod}-${statusFilters.pending}-${statusFilters.confirmed}-${statusFilters.fulfilled}-${statusFilters.cancelled}-${statusFilters.refunded}`;
  }, [selectedPeriod, statusFilters]);

  useEffect(() => {
    setNotifications([]);
    setHasMore(true);
    setInitialLoading(true);
    setHiddenGroupIds([]);
    // Reset cursor to future date for fetching newest notifications first
    cursorRef.current = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();
    loadData(true);
  }, [filterKey]);

  useEffect(() => {
    if (!sentinelRef.current || !notificationRef.current) return;
    if (initialLoading || loading || !hasMore || activeTab !== "activity") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      {
        root: notificationRef.current,
        rootMargin: "0px",
        threshold: 0.2,
      }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [initialLoading, loading, hasMore, loadMore, activeTab]); // Removed refs from dependencies

  const [clearAllNotificationsMutation] = useMutation(CLEAR_ALL_NOTIFICATION, {
    onCompleted: () => {
      apolloClient.cache.evict({ fieldName: "current" });
      apolloClient.cache.evict({ fieldName: "past" });
      apolloClient.cache.evict({ fieldName: "currentAll" });
      apolloClient.cache.evict({ fieldName: "pastAll" });
      apolloClient.cache.gc();
      setNotifications([]);
      setHasMore(true);
      setInitialLoading(true);
      setHiddenGroupIds([]);
      // Reset cursor to future date for fetching newest notifications first
      cursorRef.current = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();
      loadData(true);
    },
    onError: (error) => {
      console.error("Clear all failed", error);
    },
  });

  const handleClearAll = async () => {
    const tempData = { ...globalState };
    tempData.notificationCount = { recent: 0, past: 0 };
    handleGlobalState(tempData);

    try {
      await clearAllNotificationsMutation({
        variables: { profileId: globalState?.profile?.id },
      });
    } catch {
      // error handled in mutation onError
    }
  };

  const [updateReadStatusBatch] = useMutation(UPDATE_READ_STATUS_BATCH, {
    fetchPolicy: "no-cache",
  });

  const handleGroupedNotificationClick = (group: GroupedNotification) => {
    const tempState = { ...globalState };
    tempState.notification.text = group.title;
    tempState.notification.notificationClicked = true;
    tempState.notification.ticketId = group?.ticket?.t_id ?? null;
    tempState.notification.tpId = group?.ticket_placement?.tp_id ?? null;
    tempState.notification.tp_id_array = group?.tp_id_array ?? null;
    tempState.notification.notificationSearchCancel =
      !globalState.notification.notificationSearchCancel;
    handleGlobalState(tempState);

    if (group.feedIds.length > 0) {
      updateReadStatusBatch({ variables: { feedIds: group.feedIds } });
    }

    navigate("/managetickets");
    closePopup();
  };

  const handleHideGroupedNotification = async (
    event: MouseEvent<HTMLButtonElement>,
    group: GroupedNotification
  ) => {
    event.stopPropagation();
    setHiddenGroupIds((prev) => [...prev, group.groupId]);

    const tempData = { ...globalState };
    const isRecent =
      new Date(group.notification).getTime() >
      Date.now() - 30 * 24 * 60 * 60 * 1000;
    tempData.notificationCount = {
      recent: isRecent
        ? Math.max(globalState.notificationCount.recent - group.count, 0)
        : globalState.notificationCount.recent,
      past: !isRecent
        ? Math.max(globalState.notificationCount.past - group.count, 0)
        : globalState.notificationCount.past,
    };
    handleGlobalState(tempData);

    if (group.feedIds.length > 0) {
      await updateReadStatusBatch({ variables: { feedIds: group.feedIds } });
    }
  };

  const getTimeLabel = (notificationDate: string): string => {
    const now = moment.utc();
    const target = moment(notificationDate);
    const diffMinutes = now.diff(target, "minutes");
    if (diffMinutes < 60) {
      return `${diffMinutes} mins ago`;
    }
    const diffHours = now.diff(target, "hours");
    if (diffHours < 24) {
      return `${diffHours} hours ago`;
    }
    const diffDays = now.startOf("day").diff(target.startOf("day"), "days");
    return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
  };

  const getListingAlertDetails = (feed: notificationData) => {
    if (feed?.type !== "Added") return null;
    const ticketUser = feed?.ticket?.user;
    const notes = (ticketUser?.user_notes ?? []).map((note: any) => ({
      n_type: note?.noteType,
      n_content: note?.noteContent,
    }));
    const internalNotes = formatNotesForDisplay(notes, "internal");
    const externalNotes = formatNotesForDisplay(notes, "external");
    return {
      customerType: formatCustomerType(ticketUser?.pricingMode),
      internalNotes,
      externalNotes,
      hasInternalNotes: internalNotes !== "N/A",
      hasExternalNotes: externalNotes !== "N/A",
    };
  };

  const getStatusBucket = (feedType?: string): keyof typeof statusFilters | null => {
    const normalized = String(feedType || "").toLowerCase();
    if (
      normalized === "added" ||
      normalized === "delistinprogress" ||
      normalized === "pending"
    )
      return "pending";
    if (normalized === "list" || normalized === "confirmed") return "confirmed";
    if (normalized === "sold" || normalized === "fulfilled") return "fulfilled";
    if (
      normalized === "delist" ||
      normalized === "deleteuser" ||
      normalized === "cancelled" ||
      normalized === "canceled"
    )
      return "cancelled";
    if (normalized === "refunded") return "refunded";
    return null;
  };

  const filterByStatus = (items: notificationData[] = []) =>
    items.filter((item) => {
      const bucket = getStatusBucket(item.type);
      if (!bucket) return true;
      return statusFilters[bucket];
    });

  const filterByPeriod = (items: notificationData[] = []) =>
    items.filter((item) => {
      const itemDate = new Date(item.notification).getTime();
      const sinceDateMs = new Date(getSinceDateByPeriod(selectedPeriod)).getTime();
      return Number.isFinite(itemDate) && itemDate >= sinceDateMs;
    });

  const dedupeById = (items: notificationData[] = []) => {
    const seen = new Set<string>();
    return items.filter((item) => {
      if (!item?.f_id || seen.has(item.f_id)) return false;
      seen.add(item.f_id);
      return true;
    });
  };

  const groupNotifications = (
    items: notificationData[] = []
  ): GroupedNotification[] => {
    const groups = new Map<string, GroupedNotification>();
    items.forEach((item) => {
      if (!item) return;
      let groupKey = "";
      if (
        item.type === "Added" ||
        item.type === "List" ||
        item.type === "Sold" ||
        item.type === "Delist" ||
        item.type === "DelistInProgress"
      ) {
        groupKey = `${item.type}_${item?.ticket?.event?.e_id || "unknown"}`;
      } else if (item.type === "DeleteUser") {
        const userId = (item?.ticket?.user as any)?.u_id || item?.u_id || "unknown";
        groupKey = `DeleteUser_${userId}`;
      } else {
        groupKey = `${item.type}_${item?.ticket?.t_id || "unknown"}`;
      }

      if (groups.has(groupKey)) {
        const existing = groups.get(groupKey)!;
        existing.count += 1;
        existing.feedIds.push(item.f_id);
        if (new Date(item.notification) > new Date(existing.notification)) {
          existing.notification = item.notification;
          existing.latestFeedId = item.f_id;
          existing.ticket = item.ticket;
          existing.ticket_placement = item.ticket_placement;
          existing.tp_id_array = item.tp_id_array;
        }
      } else {
        groups.set(groupKey, {
          groupId: groupKey,
          type: item.type,
          title: item.title || "",
          count: 1,
          feedIds: [item.f_id],
          notification: item.notification,
          ticket: item.ticket,
          ticket_placement: item.ticket_placement,
          tp_id_array: item.tp_id_array,
          latestFeedId: item.f_id,
        });
      }
    });
    return Array.from(groups.values());
  };

  const filteredNotifications = useMemo(() => {
    return dedupeById(filterByPeriod(filterByStatus(notifications)));
  }, [notifications, statusFilters, selectedPeriod]);

  const groupedNotifications = useMemo(
    () => groupNotifications(filteredNotifications),
    [filteredNotifications]
  );

  const visibleGrouped = groupedNotifications.filter(
    (group) => !hiddenGroupIds.includes(group.groupId)
  );

  useEffect(() => {
    const recent = visibleGrouped.filter(
      (g) => new Date(g.notification).getTime() > Date.now() - 30 * 24 * 60 * 60 * 1000
    );
    const past = visibleGrouped.filter(
      (g) => new Date(g.notification).getTime() <= Date.now() - 30 * 24 * 60 * 60 * 1000
    );
    const totalRecent = recent.reduce((sum, g) => sum + g.count, 0);
    const totalPast = past.reduce((sum, g) => sum + g.count, 0);
    const tempData = { ...globalState };
    tempData.notificationCount = { recent: totalRecent, past: totalPast };
    handleGlobalState(tempData);
  }, [visibleGrouped, globalState, handleGlobalState]);

  return (
    <div className={styles.notification}>
      <div className={styles.topTabs}>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === "activity" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("activity")}
        >
          Activity
        </button>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === "summary" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("summary")}
        >
          Summary
        </button>
        {!initialLoading && visibleGrouped.length > 0 && (
          <button type="button" className={styles.clearAllBtn} onClick={handleClearAll}>
            Clear all
          </button>
        )}
      </div>

      <div className={styles.contentArea} ref={notificationRef}>
        {initialLoading && (
          <div className={styles.loader}>
            <CircularProgress color="inherit" />
          </div>
        )}

        {!initialLoading && activeTab === "activity" && (
          <div className={styles.notificationList}>
            {visibleGrouped.length === 0 ? (
              <p className={styles.emptyState}>No notifications yet</p>
            ) : (
              visibleGrouped.map((group) => {
                const dummyFeed: notificationData = {
                  f_id: group.latestFeedId,
                  type: group.type,
                  title: group.title,
                  u_id: "",
                  notification: group.notification,
                  user_feeds: [],
                  ticket: group.ticket,
                  ticket_placement: group.ticket_placement,
                  tp_id_array: group.tp_id_array,
                };
                const listingDetails = getListingAlertDetails(dummyFeed);

                return (
                  <div
                    role="button"
                    tabIndex={0}
                    key={group.groupId}
                    className={`${styles.notificationItem} ${styles.groupedNotification}`}
                    onClick={() => handleGroupedNotificationClick(group)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        handleGroupedNotificationClick(group);
                      }
                    }}
                  >
                    <img
                      className={styles.avatar}
                      alt="event"
                      src={
                        group?.ticket?.event?.eventImage
                          ? group.ticket.event.eventImage
                          : DefaultProfile
                      }
                    />
                    <div className={styles.messageContent}>
                      <p className={styles.typeBadge}>
                        {getNotificationTypeLabel(group?.type)}
                        {group.count > 1 && (
                          <span className={styles.countBadge}> ×{group.count}</span>
                        )}
                      </p>
                      <p
                        className={`${styles.title} ${
                          group?.type === "DeleteUser" ? styles.deleteTitle : ""
                        }`}
                      >
                        {group?.title ?? ""}
                        {group.count > 1 && (
                          <span className={styles.countText}>
                            ({group.count} tickets)
                          </span>
                        )}
                      </p>
                      <p className={styles.subTitle}>
                        {`${group?.ticket?.event?.eventName ?? ""} ${
                          group?.ticket?.event?.eventDate
                            ? moment(group.ticket.event.eventDate)
                                .tz("America/Chicago")
                                .format("ddd DD MMM, hh:mm A z")
                            : ""
                        }`}
                      </p>
                      <p className={styles.subTitle}>
                        {group?.ticket?.event?.eventAddress ?? ""}
                      </p>
                      {listingDetails && (
                        <>
                          <p className={styles.metaLine}>
                            <span className={styles.metaLabel}>Customer type:</span>{" "}
                            {listingDetails.customerType}
                          </p>
                          <p
                            className={`${styles.metaLine} ${
                              listingDetails.hasInternalNotes ? styles.noteHighlight : ""
                            }`}
                          >
                            <span className={styles.metaLabel}>Internal notes:</span>{" "}
                            {truncateNotePreview(listingDetails.internalNotes)}
                          </p>
                          <p
                            className={`${styles.metaLine} ${
                              listingDetails.hasExternalNotes ? styles.noteHighlight : ""
                            }`}
                          >
                            <span className={styles.metaLabel}>External notes:</span>{" "}
                            {truncateNotePreview(listingDetails.externalNotes)}
                          </p>
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.closeNotificationBtn}
                      aria-label="Dismiss notification"
                      onClick={(event) => handleHideGroupedNotification(event, group)}
                    >
                      <IoClose size={16} />
                    </button>
                    <span className={styles.time}>{getTimeLabel(group.notification)}</span>
                  </div>
                );
              })
            )}

            {hasMore && (
              <div
                ref={sentinelRef}
                style={{
                  height: "20px",
                  margin: "10px 0",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {loading && <CircularProgress size={24} color="inherit" />}
              </div>
            )}
            {!hasMore && notifications.length > 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "10px 0",
                  color: "#888",
                  fontSize: "0.9rem",
                }}
              >
                All notifications loaded
              </div>
            )}
          </div>
        )}

        {!initialLoading && activeTab === "summary" && (
          <div className={styles.summaryBlock}>
            <div className={styles.summaryRow}>
              <span>Total Notifications</span>
              <strong>{recentCount + pastCount}</strong>
            </div>
            <div className={styles.summaryRow}>
              <span>Recent</span>
              <strong>{recentCount}</strong>
            </div>
            <div className={styles.summaryRow}>
              <span>Past</span>
              <strong>{pastCount}</strong>
            </div>
          </div>
        )}
      </div>

      <div className={styles.periodButtons}>
        {["30D", "90D", "12M", "All"].map((period) => (
          <button
            key={period}
            type="button"
            className={`${styles.periodBtn} ${
              selectedPeriod === period ? styles.activePeriod : ""
            }`}
            onClick={() =>
              setSelectedPeriod(period as "30D" | "90D" | "12M" | "All")
            }
          >
            {period}
          </button>
        ))}
      </div>
    </div>
  );
};

export default NotificationComponent;